import { Group, Stack, Switch } from '@mantine/core'
import Select from 'app/views/components/inputs/Select'
import { UseFormReturnType } from '@mantine/form'
import {
  EntityThirdPartyProviders
} from 'app/models/entity-params'
import { thirdPartyProviderRoleOptions } from './entity-profile/components/entity-profile-form/roles-section'
import { EntityProfileParams, EntityRole, ThirdPartyRoles } from 'app/models/entity-profile-params'
import IonIcon from '@reacticons/ionicons'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import FormWrapper from 'app/views/components/Form/FormWrapper'
import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { getEntities } from 'app/state/ducks/entities/selectors'


type EntitiesFormProps = {
  entityProfiles?: EntityProfileParams[]
  form: UseFormReturnType<any>
  isLoading: boolean;
  isStepper?: boolean;
  mandatoryFields: string[]
  submitForm: any;
  transformValues: (values: Record<string, any>) => any;
  submitAndExit: () => void;
  handleBulkNotificationAndClosure: () => void;
}

export default function EntitiesFormThirdPartyProviders({
  entityProfiles,
  form,
  isLoading,
  submitAndExit,
  submitForm,
}: EntitiesFormProps) {

  const hasInsertedInitial = useRef(false);
  const entities = useSelector(getEntities)

  useEffect(() => {
    if (!hasInsertedInitial.current && form.values.thirdPartyProviders.length === 0) {
      form.insertListItem('thirdPartyProviders', {
        thirdPartyRole: '',
        thirdPartyProfile: { id: '', admin: '' },
        deliverDocs: false,
      });
      hasInsertedInitial.current = true;
    }
  }, []);

  const getProfileOptions = (role: ThirdPartyRoles): { label: string; value: string }[] => {
    if (!entities || !entityProfiles) return [];

    return entities
      .map((entity) => {
        const matchingProfile = entity.entityProfile.find((profileKey) =>
          entityProfiles.some(
            (profile) => profile.id === profileKey.id && profile.roles.includes(role as string as EntityRole)
          )
        );

        if (!matchingProfile) return null;

        const profile = entityProfiles.find(p => p.id === matchingProfile.id);

        return {
          label: `${entity.entityName}${profile ? ` (${profile.name})` : ''}`,
          value: JSON.stringify({ id: entity.id, admin: entity.entityType?.admin ?? '' }),
        };
      })
      .filter((option): option is { label: string; value: string } => option !== null);
  };

  const getAvailableProvidersForRow = (role: ThirdPartyRoles, currentIndex: number): { label: string; value: string; disabled?: boolean }[] => {
    const allOptions = getProfileOptions(role);

    // Get all selected providers for this specific role in other rows
    const selectedProviderIds = form.values.thirdPartyProviders
      .map((provider: EntityThirdPartyProviders, index: number) =>
        index !== currentIndex && provider.thirdPartyRole === role
          ? provider.thirdPartyProfile?.id
          : null
      )
      .filter((id: string | null) => id !== null);

    // Mark providers as disabled if they're already selected for this role in other rows
    return allOptions.map(option => {
      const parsed = JSON.parse(option.value);
      return {
        ...option,
        disabled: selectedProviderIds.includes(parsed.id),
      };
    });
  };



  type ProviderError = {
    thirdPartyRole?: string;
    thirdPartyProfile?: string;
  };

  type FormErrors = {
    thirdPartyProviders?: ProviderError[];
  };

  const getProviderDeliveryMethod = (providerId: string, role: ThirdPartyRoles): string => {
    if (!providerId || !role) return 'N/A';

    const providerEntity = entities.find(entity => entity.id === providerId);
    if (!providerEntity || !providerEntity.deliverySettings) return 'N/A';

    const deliverySetting = providerEntity.deliverySettings.find(
      (setting: any) => setting.deliveryRole === role
    );

    return deliverySetting?.deliveryMethod || 'N/A';
  };

  const fields = form.values?.thirdPartyProviders?.map((c: EntityThirdPartyProviders, c_i: number) => {
    const roleSelected = !!c.thirdPartyRole;
    const profileFieldPath = `thirdPartyProviders.${c_i}.thirdPartyProfile`;

    const profileOptions = getAvailableProvidersForRow(c.thirdPartyRole, c_i);
    const noProfiles = roleSelected && profileOptions.length === 0;

    const errors = form.errors as FormErrors;
    const profileError =
      noProfiles ||
      (form.isTouched(profileFieldPath) && errors.thirdPartyProviders?.[c_i]?.thirdPartyProfile);

    const providerDeliveryMethod = getProviderDeliveryMethod(
      c.thirdPartyProfile?.id,
      c.thirdPartyRole
    );

    return (
      <div key={'thirdPartyForm' + c_i}>
        <form>
          <Stack>
            <Group noWrap w="100%">
              <Select
                label="Role"
                placeholder="Select role"
                searchable
                w="33%"
                data={thirdPartyProviderRoleOptions}
                {...form.getInputProps(`thirdPartyProviders.${c_i}.thirdPartyRole`)}
                onChange={(value) => {
                  form.setFieldValue(`thirdPartyProviders.${c_i}.thirdPartyRole`, value);
                  form.setFieldValue(`thirdPartyProviders.${c_i}.thirdPartyProfile`, { id: '', admin: '' });
                  // Checks form validation which will trigger the error message for no profiles
                  form.validate();
                }}
              />

              <Select
                searchable
                w="33%"
                label="Provider"
                placeholder={noProfiles ? 'No Providers Found' : 'Select Provider'}
                data={profileOptions}
                value={
                  c.thirdPartyProfile?.id
                    ? JSON.stringify({ id: c.thirdPartyProfile.id, admin: c.thirdPartyProfile.admin })
                    : ''
                }
                onChange={(value) => {
                  const parsed = value ? JSON.parse(value) : { id: '', admin: '' };

                  form.setFieldValue(profileFieldPath, {
                    id: parsed.id,
                    admin: parsed.admin,
                  });

                  form.setTouched({ [profileFieldPath]: true });
                }}

                onBlur={() => form.setTouched({ [profileFieldPath]: true })}
                error={noProfiles ? true : profileError}
              />

              <Stack spacing="xs" style={{ marginTop: '-7px' }}>
                <span className="form-label">Deliver Docs</span>
                <Switch
                  size="lg"
                  checked={c.deliverDocs}
                  {...form.getInputProps(`thirdPartyProviders.${c_i}.deliverDocs`)}
                />
              </Stack>

              <Stack spacing="xs" w="33%">
                <span className="form-label">Delivery Method</span>
                <div className={`delivery-method-display ${providerDeliveryMethod === 'N/A' ? 'na' : 'set'}`}>
                  {providerDeliveryMethod}
                </div>
              </Stack>

              {(form.values.thirdPartyProviders.length > 0) &&
                <Group noWrap mt='25px'>
                  <Group noWrap w={'75%'}></Group>
                  <Group noWrap w={'100%'}>
                    <IonIcon name="trash-outline" className="click-ion-icon" style={{ color: "red" }} onClick={() => form.removeListItem('thirdPartyProviders', c_i)} />
                    <span className="remove-button-text">Remove</span>
                  </Group>
                </Group>
              }
            </Group>
          </Stack>

        </form>
        {(form.values.thirdPartyProviders.length > 1) &&
          <Group noWrap className="form-row-divider"></Group>
        }
      </div>
    )
  })

  return (
    <FormWrapper
      title={`Third Party Providers`}>
      <div className="content">
        <>{fields}</>


        <Stack style={{ marginTop: form.values.thirdPartyProviders.length > 0 ? '15px' : '0px', }}>
          <Group noWrap>
            <div className="add-action-container">
              <IonIcon name="add-circle-outline" className="click-ion-icon"
                onClick={() =>
                  form.insertListItem('thirdPartyProviders', {
                    thirdPartyRole: '',
                    thirdPartyProfile: {
                      admin: '',
                      id: ''
                    },
                    deliverDocs: false,
                  })
                } />
              <span className="add-action-text">Add Provider</span>
            </div>
          </Group>
        </Stack>


        <Stack>
          <div
            style={{
              marginTop: '1rem',
              display: 'flex',
              gap: '.5rem',
              justifyContent: 'flex-end',
            }}
          >
            <Group noWrap w="100%">
              <PrimaryButton
                disabled={!form.isValid()}
                className="form-button"
                loading={isLoading}
                onClick={(event) => submitForm(event)}
                w="100%"
              >
                Save and Proceed
              </PrimaryButton>
              <PrimaryButton
                disabled={!form.isValid()}
                className="form-button"
                loading={isLoading}
                onClick={() => submitAndExit()}
                w="100%"
              >
                Save and Exit
              </PrimaryButton>
            </Group>
          </div>
        </Stack>
      </div>
    </FormWrapper>
  )
}
